import { useQuery } from '@tanstack/react-query'
import { dynamoOpenFriendDm } from './projectDynamo'
import { listConversations } from '../chatv1/transport'
import { getFriends, type ProjectRow } from '../transport/api'
import { dmKey, isDmConv } from '../urlState'

/**
 * Resolve a friend's email → the MANAGED friend-DM conversation, handed back as a
 * `ProjectRow` ready for `<ProjectPanel locked>`.
 *
 * This is the replacement for the E2E `useEncryptedDm` lane. The old lane could not open a
 * room until the PEER had published a key — the "Encrypted messaging unavailable, they
 * haven't opened encrypted messaging yet" gate the owner asked us to retire. The managed
 * conv_id is DERIVED from the sorted friend pair, so the room exists the instant EITHER
 * side opens it: create-or-resolve is idempotent and both friends always land on the same
 * id. Friendship is enforced server-side (403 `not_friends` / 404 `unknown_peer`), surfaced
 * here as `error` so the caller can show it instead of a blank panel.
 *
 * The peer's display name rides on the row (resolved from the same `['friends']` cache the
 * sidebar uses) so the locked panel's title reads as the friend, not a raw conv id.
 */
export function useManagedFriendDm(
  peerOrKey: string | null,
  /** Which friend, when the address is two of them — the friend list's `account` of the row opened. */
  accountHint: string | null = null,
): { row: ProjectRow | null; loading: boolean; error: unknown } {
  // Reuse the shared friends cache (same queryKey as the sidebar) for the display
  // name AND to reverse a URL dmKey back to the email — no extra fetch when warm.
  const friendsQ = useQuery({ queryKey: ['friends'], queryFn: getFriends, enabled: !!peerOrKey })
  // A group-mate who is not a friend (owner, 2026-10-18: members of one group DM without being friends) is
  // on no friends list — but the chat's own DM list knows them: its rows carry the peer's address and name.
  // Same key and fetch as the app's DM list, so this is a second reader of that cache.
  const dmsQ = useQuery({
    queryKey: ['dm-conversations'],
    queryFn: () => listConversations('dm'),
    enabled: !!peerOrKey,
  })
  const friends = friendsQ.data?.friends ?? []
  const dmRows = dmsQ.data?.conversations ?? []
  const dmPeers = dmRows.flatMap((c) => (c.peer ? [c.peer] : []))
  // A dm named by its conversation id (a group-mate's, who has no address here): it already exists — the BFF
  // opened it, or a notification named it — so there is nothing to create; its name is its peer's.
  const byConv = isDmConv(peerOrKey) ? peerOrKey : null
  // A URL carries the opaque dmKey; an in-app open carries the email. Resolve a key
  // to its email via the friends list, then the DM list — null until they load, which keeps
  // the panel loading a beat longer on a cold ?dm=<key> link rather than flashing "not found".
  const peer =
    peerOrKey == null || byConv
      ? null
      : peerOrKey.includes('@')
        ? peerOrKey
        : (friends.find((f) => dmKey(f.email) === peerOrKey)?.email ??
          dmPeers.find((p) => dmKey(p.email) === peerOrKey)?.email ??
          null)
  /*
   * WHICH FRIEND, NOT ONLY WHICH ADDRESS — owner, 2026-10-31: 使用者直聊好像不 work. He is friends with
   * both accounts at one address (a Google one and an Apple one), and a dm opened by address alone was
   * refused as unknown_peer — correctly, the address names two people. The row he tapped says which
   * (`accountHint`); when nothing does, an address that is ONE friend still names that friend's account.
   */
  const atAddress = peer ? friends.filter((f) => f.email === peer) : []
  const account = accountHint ?? (atAddress.length === 1 ? (atAddress[0]?.account ?? null) : null)
  const peerName = peer
    ? ((account ? friends.find((f) => f.account === account)?.name : undefined) ??
      atAddress[0]?.name ??
      dmPeers.find((p) => p.email === peer)?.name ??
      null)
    : null
  const q = useQuery({
    queryKey: ['friend-dm', peer, account],
    queryFn: () => dynamoOpenFriendDm(peer as string, account),
    // A friend pair always resolves to the same conv_id — never goes stale, and one
    // create-or-resolve per opened peer is enough (react-query dedupes the rest).
    enabled: !!peer,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })
  if (byConv) {
    const name = dmRows.find((c) => c.conv_id === byConv)?.peer?.name
    return { row: { project_id: byConv, ...(name ? { name } : {}) }, loading: false, error: null }
  }
  const row: ProjectRow | null = q.data
    ? { project_id: q.data.conv_id, ...(peerName ? { name: peerName } : {}) }
    : null
  // A cold ?dm=<key> link is still "loading" while friends load and the key→email
  // resolves — not yet "not found".
  const resolving = !!peerOrKey && !peer
  return { row, loading: resolving || (!!peer && q.isPending), error: q.error }
}
